/**
 * THE LIVE WEB in an Ask (MODES.md §10.9) — what the room sees while an agent searches, and the sources its answer
 * stands on. Search-engine clear:
 *
 *   WebProgress — each search as it happens ("Searching “cmmc level 2”" → ✓ 6 results), each page being read
 *                 (site badge · title · ✓), live for everyone; while the answer types it folds to one line
 *                 ("Searched 2 · read 1 page · 5 sources" + the sites).
 *   SourceCards — under the answer: numbered cards (site · title) that open the page; [n] in the text links to them.
 *
 * Sites get a coloured LETTER badge — never a third-party favicon fetch (a viewer's browser never calls out to
 * anyone but the page they choose to open).
 */
import React from 'react';
import { Check, X, Loader2, Globe } from 'lucide-react';

const TONES = ['bg-sky-600', 'bg-emerald-600', 'bg-violet-600', 'bg-amber-600', 'bg-rose-600', 'bg-teal-600',
  'bg-indigo-600', 'bg-fuchsia-600'];

/** A site's letter badge colour — stable per host. Pure. */
export function siteTone(host = '') {
  let h = 0;
  for (const ch of String(host)) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return TONES[h % TONES.length];
}

export function SiteBadge({ host = '', size = 'sm' }) {
  const letter = (String(host).replace(/^www\./, '')[0] || '?').toUpperCase();
  const dim = size === 'xs' ? 'h-4 w-4 text-[9px]' : 'h-5 w-5 text-[10px]';
  return (
    <span aria-hidden className={`inline-flex shrink-0 items-center justify-center rounded-full font-semibold text-white ring-2 ring-white ${dim} ${siteTone(host)}`}>
      {letter}
    </span>
  );
}

/** "[2]" in an answer → a link to source 2 (code spans / fences are left alone). Pure. */
export function linkCitations(text = '', sources = []) {
  const byN = new Map((sources || []).filter((s) => s && s.n && s.url).map((s) => [Number(s.n), s]));
  if (!byN.size) return String(text || '');
  return String(text || '').split(/(```[\s\S]*?```|`[^`\n]*`)/g).map((part, i) => (i % 2 ? part
    : part.replace(/\[(\d{1,2})\](?!\()/g, (m, d) => {
      const s = byN.get(Number(d));
      return s ? `[[${d}]](${s.url} "${String(s.title || s.host || '').replace(/"/g, "'")}")` : m;
    }))).join('');
}

function StatusIcon({ status }) {
  if (status === 'running' || status === 'reading') return <Loader2 className="h-3.5 w-3.5 animate-spin text-indigo-500" aria-hidden />;
  if (status === 'failed') return <X className="h-3.5 w-3.5 text-rose-500" aria-hidden />;
  return <Check className="h-3.5 w-3.5 text-emerald-600" aria-hidden />;
}

export function WebProgress({ web = null, compact = false, testId = 'web-progress' }) {
  const queries = (web && web.queries) || [];
  const pages = (web && web.pages) || [];
  if (!queries.length && !pages.length) return null;
  const read = pages.filter((p) => p.status === 'done').length;
  const hosts = [...new Set(pages.map((p) => p.host).filter(Boolean))];
  if (compact) {
    const n = Number((web && web.sources) || 0);
    return (
      <div data-testid={testId} data-compact="true" className="mb-1 flex items-center gap-1.5 text-[0.75rem] text-gray-500">
        <Globe className="h-3.5 w-3.5 text-indigo-500" aria-hidden />
        <span>Searched {queries.length}{read ? ` · read ${read} page${read === 1 ? '' : 's'}` : ''}{n ? ` · ${n} source${n === 1 ? '' : 's'}` : ''}</span>
        <span className="ml-0.5 flex -space-x-1">{hosts.slice(0, 5).map((h) => <SiteBadge key={h} host={h} size="xs" />)}</span>
      </div>
    );
  }
  return (
    <ol data-testid={testId} className="mt-0.5 space-y-1 text-[0.8125rem]" aria-label="What the agent is looking up">
      {queries.map((q, i) => (
        <li key={`q${i}`} data-testid={`${testId}-query-${i}`} data-status={q.status} className="sf-rise flex items-center gap-2">
          <StatusIcon status={q.status} />
          <span className={q.status === 'running' ? 'sf-shimmer-text font-medium' : 'text-gray-700'}>
            {q.status === 'running' ? 'Searching' : 'Searched'} <span className="font-medium text-gray-900">“{q.q}”</span>
          </span>
          {q.status === 'done' ? <span className="text-[0.75rem] text-gray-400">{q.count} result{q.count === 1 ? '' : 's'}</span> : null}
          {q.status === 'failed' ? <span className="text-[0.75rem] text-rose-500">couldn’t reach the web</span> : null}
        </li>
      ))}
      {pages.map((p, i) => (
        <li key={`p${i}`} data-testid={`${testId}-page-${i}`} data-status={p.status} className="sf-rise flex min-w-0 items-center gap-2">
          <StatusIcon status={p.status} />
          <SiteBadge host={p.host} />
          <span className={`min-w-0 truncate ${p.status === 'reading' ? 'sf-shimmer-text font-medium' : 'text-gray-700'}`}>
            {p.status === 'reading' ? 'Reading ' : ''}<span className="text-gray-900">{p.host}</span>
            {p.title ? <span className="text-gray-500"> — {p.title}</span> : null}
          </span>
        </li>
      ))}
    </ol>
  );
}

export function SourceCards({ sources = [], testId = 'web-sources' }) {
  const list = (sources || []).filter((s) => s && s.url);
  if (!list.length) return null;
  return (
    <div data-testid={testId} className="mt-1.5">
      <div className="mb-1 flex items-center gap-1 text-[0.6875rem] font-semibold uppercase tracking-wide text-gray-500">
        <Globe className="h-3 w-3" aria-hidden />Sources
      </div>
      <div className="flex flex-wrap gap-1.5">
        {list.map((s) => (
          <a key={s.n} href={s.url} target="_blank" rel="noopener noreferrer" data-testid={`${testId}-${s.n}`}
            title={s.snippet || s.title || s.url}
            className="group/src flex w-52 min-w-0 flex-col gap-0.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 transition-colors hover:border-indigo-300 hover:bg-indigo-50/40">
            <span className="flex min-w-0 items-center gap-1.5 text-[0.6875rem] text-gray-500">
              <SiteBadge host={s.host} size="xs" />
              <span className="truncate">{s.host}</span>
              <span className="ml-auto rounded bg-gray-100 px-1 font-semibold tabular-nums text-gray-600">{s.n}</span>
            </span>
            <span className="line-clamp-2 text-[0.78125rem] font-medium leading-snug text-gray-900 group-hover/src:text-indigo-800">
              {s.title || s.url}
            </span>
          </a>
        ))}
      </div>
    </div>
  );
}
